import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  BarChart3,
  CalendarDays,
  CalendarHeart,
  ClipboardList,
  Home,
  Menu,
  MessageCircle,
  User,
  HandHeart,
  HeartHandshake,
  Users,
} from "lucide-react";
import {
  MRHome,
  MRSchedule,
  MRTasks,
  MRRequests,
  MRProfile,
  MRMessages,
  MRLeader,
  MRGroups,
  MRAvailability,
} from "@/components/icons/MemberIcons";
import { AppLayout } from "@/components/layout/AppLayout";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { useCurrentPerson } from "@/hooks/useCurrentPerson";
import { useAccessContext } from "@/hooks/useAccessContext";
import type { Module } from "@/lib/permissions";

interface VolNavItem {
  label: string;
  href: string;
  icon: typeof Home;
  prIcon?: React.ComponentType<React.ComponentPropsWithoutRef<"svg">>;
  requiredModule?: Module;
  requiresLeader?: boolean;
}

const volunteerNavigation: VolNavItem[] = [
  { label: "Overview", href: "/volunteer", icon: Home, prIcon: MRHome },
  { label: "Schedule", href: "/volunteer/schedule", icon: CalendarDays, prIcon: MRSchedule, requiredModule: "calendar" },
  { label: "Availability", href: "/volunteer/availability", icon: CalendarHeart, prIcon: MRAvailability, requiredModule: "calendar" },
  { label: "Cover a spot", href: "/volunteer/sub-requests", icon: HeartHandshake, prIcon: MRRequests, requiredModule: "calendar" },
  { label: "Tasks", href: "/volunteer/tasks", icon: ClipboardList, prIcon: MRTasks, requiredModule: "tasks" },
  { label: "Requests", href: "/volunteer/requests", icon: HandHeart, prIcon: MRRequests, requiredModule: "requests" },
  { label: "Profile", href: "/volunteer/profile", icon: User, prIcon: MRProfile },
  // Direct messages aren't gated by the "messaging" (bulk broadcast) module —
  // MemberShell's equivalent nav entry has no requiredModule either, and the
  // volunteer role's messaging capability is intentionally empty (bulk send
  // is staff-only), which would otherwise hide this item for every volunteer.
  { label: "Messages", href: "/volunteer/messages", icon: MessageCircle, prIcon: MRMessages },
  { label: "Leader services", href: "/volunteer/leader/services", icon: ClipboardList, prIcon: MRLeader, requiresLeader: true },
  { label: "Leader roster", href: "/volunteer/leader/roster", icon: Users, prIcon: MRGroups, requiresLeader: true },
  { label: "Leader requests", href: "/volunteer/leader/requests", icon: HandHeart, prIcon: MRRequests, requiresLeader: true },
  { label: "Leader reports", href: "/volunteer/leader/reports", icon: BarChart3, prIcon: MRLeader, requiresLeader: true },
];

// Primary tabs shown in the bottom nav bar on mobile
const BOTTOM_TABS: VolNavItem[] = [
  { label: "Overview", href: "/volunteer", icon: Home, prIcon: MRHome },
  { label: "Schedule", href: "/volunteer/schedule", icon: CalendarDays, prIcon: MRSchedule, requiredModule: "calendar" },
  { label: "Tasks", href: "/volunteer/tasks", icon: ClipboardList, prIcon: MRTasks, requiredModule: "tasks" },
  { label: "Requests", href: "/volunteer/requests", icon: HandHeart, prIcon: MRRequests, requiredModule: "requests" },
];

function MobileBottomNav() {
  const { data: accessContext, isLoading } = useAccessContext();
  const canAccessModule = accessContext?.capabilities.canAccessModule;
  const hasLeaderAccess = !!accessContext?.leader_ministries?.length;
  const bottomTabHrefs = new Set(BOTTOM_TABS.map((tab) => tab.href));
  const visibleTabs = isLoading
    ? BOTTOM_TABS
    : BOTTOM_TABS.filter((tab) => !tab.requiredModule || canAccessModule?.(tab.requiredModule));
  const moreItems = (isLoading ? volunteerNavigation : volunteerNavigation.filter((item) => {
    if (bottomTabHrefs.has(item.href)) return false;
    if (item.requiresLeader && !hasLeaderAccess) return false;
    if (!item.requiredModule) return true;
    return canAccessModule?.(item.requiredModule);
  }));
  const [moreOpen, setMoreOpen] = useState(false);

  return (
    <>
      <nav className="fixed inset-x-0 bottom-0 z-40 flex items-stretch border-t border-border/40 bg-background pb-[env(safe-area-inset-bottom)] md:hidden">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <NavLink
              key={tab.href}
              to={tab.href}
              end={tab.href === "/volunteer"}
              className="flex-1"
            >
              {({ isActive }) => (
                <div
                  className={cn(
                    "flex flex-col items-center justify-center gap-1 py-4 text-xs font-semibold transition-colors",
                    isActive
                      ? "text-primary"
                      : "text-muted-foreground/70",
                  )}
                >
                  <div className={cn(
                    "flex items-center justify-center rounded-full p-1.5 transition-all",
                    isActive && "bg-primary/10",
                  )}>
                    <Icon className="h-6 w-6" />
                  </div>
                  {tab.label}
                </div>
              )}
            </NavLink>
          );
        })}
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          className="flex flex-1 flex-col items-center justify-center gap-1 py-4 text-xs font-semibold text-muted-foreground/70 transition-colors"
        >
          <div className="flex items-center justify-center rounded-full p-1.5">
            <Menu className="h-6 w-6" />
          </div>
          More
        </button>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-2xl">
          <SheetHeader className="text-left">
            <SheetTitle className="text-lg">More</SheetTitle>
            <SheetDescription>
              Keep up with your volunteer commitments.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 grid grid-cols-2 min-[400px]:grid-cols-3 gap-3">
            {moreItems.map((item) => {
              const Icon = item.icon;
              const PRIcon = item.prIcon;
              return (
                <NavLink
                  key={item.href}
                  to={item.href}
                  end={item.href === "/volunteer"}
                  onClick={() => setMoreOpen(false)}
                >
                  {({ isActive }) => (
                    <div
                      className={cn(
                        "flex flex-col items-center gap-2.5 rounded-xl border p-4 text-center transition-all active:scale-95 min-h-[100px]",
                        isActive
                          ? "border-primary/30 bg-primary/5 text-primary font-semibold shadow-sm"
                          : "border-border/40 text-muted-foreground hover:border-border hover:bg-muted/30",
                      )}
                    >
                      <div className={cn(
                        "flex h-14 w-14 items-center justify-center transition-colors drop-shadow-md",
                      )}>
                        {PRIcon ? (
                          <PRIcon className="h-12 w-12" />
                        ) : (
                          <Icon className="h-8 w-8" />
                        )}
                      </div>
                      <span className="text-xs font-semibold leading-tight">
                        {item.label}
                      </span>
                    </div>
                  )}
                </NavLink>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

export function VolunteerShell() {
  const { data: person } = useCurrentPerson();
  const { data: accessContext, isLoading: accessLoading } = useAccessContext();
  const isMobile = useIsMobile();
  const hasLeaderAccess = !!accessContext?.leader_ministries?.length;
  const subtitle = person
    ? `Thanks for serving, ${person.first_name} ${person.last_name}`
    : "Your schedule, tasks, and requests";
  const canAccessModule = accessContext?.capabilities.canAccessModule;
  const visibleNavigation = accessLoading
    ? volunteerNavigation
    : volunteerNavigation.filter((item) => {
        if (item.requiresLeader && !hasLeaderAccess) return false;
        if (!item.requiredModule) return true;
        return canAccessModule?.(item.requiredModule);
      });

  return (
    <AppLayout title="Volunteer Hub" titleIsShell subtitle={subtitle} showSidebar={false}>
      <div className="space-y-6">
        <div className={cn("grid gap-6", !isMobile && "lg:grid-cols-[220px_1fr]")}>
          {/* Desktop sidebar nav */}
          {!isMobile && (
            <nav className="flex flex-col gap-2">
              {visibleNavigation.map((item) => (
                <NavLink key={item.href} to={item.href} end={item.href === "/volunteer"}>
                  {({ isActive }) => (
                    <div
                      className={cn(
                        buttonVariants({ variant: "ghost" }),
                        "w-full justify-start text-sm font-medium",
                        isActive
                          ? "bg-muted text-foreground"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      {item.label}
                    </div>
                  )}
                </NavLink>
              ))}
            </nav>
          )}
          <div className={cn("min-w-0", isMobile && "pb-20")}>
            <Outlet />
          </div>
        </div>
      </div>

      {/* Mobile bottom nav */}
      {isMobile && <MobileBottomNav />}
    </AppLayout>
  );
}
